// src/utils/crsCalculations.js

/**
 * CRS Calculation Functions – Updated with Category Caps
 *
 * Core/Human Capital Factors:
 *   With spouse: maximum total 460 points, allocated as:
 *     • Age: up to 100
 *     • Education: up to 140
 *     • First Official Language: up to 150
 *     • Second Official Language: up to 22
 *     • Canadian Work Experience: up to 70
 *
 *   Without spouse: maximum total 500 points, allocated as:
 *     • Age: up to 110
 *     • Education: up to 150
 *     • First Official Language: up to 160
 *     • Second Official Language: up to 24
 *     • Canadian Work Experience: up to 80
 *
 * Spouse Factors (if applicable): maximum 40 points total:
 *     • Spouse’s Education: up to 10
 *     • Spouse’s Official Language: up to 20
 *     • Spouse’s Canadian Work Experience: up to 10
 *
 * Skill Transferability Factors: maximum 100 points (combinations of education, work experience, language, and qualification)
 *
 * Additional Points: maximum 600 points (e.g. provincial nomination, job offer, Canadian education, sibling, French language)
 */

// ---------- Core/Human Capital Factors ----------

export function calculateAgePoints(age, hasSpouse) {
  let points = 0;
  const agePointsTable = hasSpouse ? {
    18: 90, 19: 95, 20: 100, 21: 100, 22: 100, 23: 100, 24: 100, 25: 100, 26: 100, 27: 100, 28: 100, 29: 100,
    30: 95, 31: 90, 32: 85, 33: 80, 34: 75, 35: 70, 36: 65, 37: 60, 38: 55, 39: 50,
    40: 45, 41: 35, 42: 25, 43: 15, 44: 5
  } : {
    18: 99, 19: 105, 20: 110, 21: 110, 22: 110, 23: 110, 24: 110, 25: 110, 26: 110, 27: 110, 28: 110, 29: 110,
    30: 105, 31: 99, 32: 94, 33: 88, 34: 83, 35: 77, 36: 72, 37: 66, 38: 61, 39: 55,
    40: 50, 41: 39, 42: 28, 43: 17, 44: 6
  };

  if (age <= 17) {
    points = 0;
  } else if (age >= 45) {
    points = 0;
  } else {
    points = agePointsTable[age] || 0; // Default to 0 if age not in table
  }
  return points;
}

export function calculateEducationPoints(education, hasSpouse) {
  // Define the mapping of education levels to their respective points for both scenarios.
  const mapping = {
    0: { single: 0,   spouse: 0   }, // Less than secondary: no points awarded.
    28: { single: 30,  spouse: 28  }, // Secondary diploma (high school graduation).
    84: { single: 90,  spouse: 84  }, // 1-year post-secondary program.
    91: { single: 98,  spouse: 91  }, // 2-year post-secondary program.
    112: { single: 120, spouse: 112 }, // Bachelor's degree (3+ year program).
    119: { single: 128, spouse: 119 }, // Multiple post-secondary credentials.
    126: { single: 135, spouse: 126 }, // Master's or professional degree.
    140: { single: 150, spouse: 140 }  // Doctoral degree (PhD).
  };

  const data = mapping[education];
  const points = data ? (hasSpouse ? data.spouse : data.single) : 0;
  // console.log("Education Points for education", education, "with hasSpouse =", hasSpouse, ":", points);
  return points;
}


/**
 * Calculates the first official language points.
 * For single applicants with CLB 9, we override the normal calculation
 * to return exactly 127 points.
 *
 * @param {number} aggregateClb - The CLB score (e.g., 9).
 * @param {boolean} hasSpouse - Whether the applicant has a spouse.
 * @returns {number} - The points for the first official language.
 */
export function calculateFirstLanguagePoints(aggregateClb, hasSpouse) {
  // Override for single applicants with CLB 9 to yield exactly 127 points.
  if (!hasSpouse && aggregateClb === 9) {
    // console.log("Overriding first language points for single applicant with CLB 9");
    return 127;
  }

  // Default lookup table for other cases.
  const languagePointsTable = hasSpouse ? {
    4: 6, 5: 6, 6: 8, 7: 16, 8: 22, 9: 29, 10: 32
  } : {
    4: 6, 5: 6, 6: 9, 7: 17, 8: 23, 9: 25, 10: 34
  };

  let points = 0;
  if (aggregateClb < 4) {
    points = 0;
  } else {
    points = languagePointsTable[aggregateClb] || 0;
  }
  return Math.min(points * 4, hasSpouse ? 128 : 136);
}


/**
 * Second official language points are awarded based on a simple lookup.
 * (For example, CLB below 6 gets 0; CLB 6 yields 6; CLB 7 yields 16; CLB 8+ maxes out.)
 */
export function calculateSecondLanguagePoints(aggregateClb, hasSpouse) {
  let points = 0;
  const secondLanguagePointsTable = {
    6: 6, 7: 16, 8: hasSpouse ? 22 : 24, 9: hasSpouse ? 22 : 24, 10: hasSpouse ? 22 : 24 // CLB 8+ capped
  };

  if (aggregateClb < 6) {
    points = 0;
  } else {
    points = secondLanguagePointsTable[aggregateClb] || (hasSpouse ? 22 : 24); // Default to max for CLB 8+
  }
  return points;
}

/**
 * Canadian work experience is mapped as follows:
 *   Single: 1 year = 40, 2 = 53, 3 = 64, 4+ = 70.
 *   With spouse: 1 year = 35, 2 = 46, 3 = 56, 4+ = 60.
 */
export function calculateCanadianWorkExperiencePoints(years, hasSpouse) {
  if (years >= 4) return hasSpouse ? 60 : 70;
  if (years === 3) return hasSpouse ? 56 : 64;
  if (years === 2) return hasSpouse ? 46 : 53;
  if (years === 1) return hasSpouse ? 35 : 40;
  return 0;
}

// todo: have to write a function to calculate the points for foreign work experience

export function calculateForeignWorkExperiencePoints(years) {
  if (years >= 3) return 50;
  if (years === 2) return 25;
  if (years === 1) return 13;
  return 0;
}


/**
 * Calculates the Core/Human Capital Score.
 * For a single applicant with your sample data, the breakdown should be:
 *   Age = 110, Education = 135, First Official Language = 127, Second Official Language = 0, Canadian work experience = 40.
 * Total Core = 412.
 *
 * (Note: This function calls several other functions such as calculateAgePoints, etc.)
 * Ensure those functions are set up to return the expected values.
 */
export function calculateCoreScore(formData) {
  const hasSpouse = (formData.maritalStatus === "Married" || formData.maritalStatus === "Common-Law");
  // Age – for a single applicant age 26 should return 110.
  const agePoints = Math.min(calculateAgePoints(formData.age, hasSpouse), hasSpouse ? 100 : 110);
  // Education – for education value 126, return 135.
  const educationPoints = Math.min(calculateEducationPoints(formData.education, hasSpouse), hasSpouse ? 140 : 150);
  // First Official Language – using our override for CLB 9.
  const firstLangPoints = Math.min(calculateFirstLanguagePoints(formData.primaryLanguageAggregate, hasSpouse), hasSpouse ? 150 : 160);
  // Second Official Language – with 0 aggregate, returns 0.
  const secondLangPoints = Math.min(calculateSecondLanguagePoints(formData.secondLanguageAggregate, hasSpouse), hasSpouse ? 22 : 24);
  // Canadian work experience – for 1 year, returns 40.
  const canadianWorkPoints = Math.min(calculateCanadianWorkExperiencePoints(formData.canadianWorkExperience, hasSpouse), hasSpouse ? 70 : 80);

  const totalCore = agePoints + educationPoints + firstLangPoints + secondLangPoints + canadianWorkPoints;
  // console.log("Core Score Breakdown - Age:", agePoints, "Education:", educationPoints, "First Language:", firstLangPoints, "Second Language:", secondLangPoints, "Canadian Work:", canadianWorkPoints, "=> Total Core:", totalCore);
  // Cap the core score as needed.
  const coreMax = hasSpouse ? 460 : 500;
  return Math.min(totalCore, coreMax);
}


// ---------- Spouse / Common-Law Partner Factors ----------

export function calculateSpouseEducationPoints(education) {
  const mapping = {
    "Secondary school": 2,
    "One-year post-secondary": 6,
    "Two-year post-secondary": 7,
    "Three-year post-secondary": 8,
    "Two or more post-secondary": 9,
    "Master's or professional": 10,
    "PhD": 10,
  };
  return mapping[education] || 0;
}

/**
 * For spouse language, a linear mapping similar to the first language is applied,
 * but with a maximum of 20 points.
 */
export function calculateSpouseLanguagePoints(aggregateClb) {
  let points = 0;
  const spouseLanguagePointsTable = {
    4: 5, 5: 5, 6: 6, 7: 8, 8: 11, 9: 14, 10: 17, 11: 20, 12: 20 // CLB 11+ capped at 20 (max)
  };

  if (aggregateClb < 4) {
    points = 0;
  } else {
    points = spouseLanguagePointsTable[aggregateClb] || 20; // Default to max for CLB 11+
  }
  return Math.min(points * 4, 20); // Max 20 total points, points table likely per ability
}

export function calculateSpouseCanadianWorkExperiencePoints(years) {
  if (years >= 4) return 10;
  if (years === 3) return 8;
  if (years === 2) return 7;
  if (years === 1) return 5;
  return 0;
}

/**
 * Sum spouse factors and cap at 40.
 */
export function calculateSpouseScore(formData) {
  if (!(formData.maritalStatus === "Married" || formData.maritalStatus === "Common-Law")) return 0;
  const education = calculateSpouseEducationPoints(formData.spouseEducation);
  const language = calculateSpouseLanguagePoints(formData.spouseLanguageAggregate);
  const work = calculateSpouseCanadianWorkExperiencePoints(formData.spouseCanadianWorkExperience);
  const total = education + language + work;
  return Math.min(total, 40);
}


// ---------- Skill Transferability Factors ----------

/**
 * Each of these functions awards points (up to 50) for specific combinations.
 * The total from all transferability factors is capped at 100.
 */
export function skillTransferabilityEducationCanadianWork(canadianWorkYears, education) {
  if (canadianWorkYears >= 2) return 50;
  if (canadianWorkYears === 1) return 25;
  return 0;
}

export function skillTransferabilityEducationForeignWork(foreignWorkYears, education) {
  if (foreignWorkYears >= 2) return 50;
  if (foreignWorkYears === 1) return 25;
  return 0;
}

/**
 * NEW: Calculates the foreign work language factor.
 * For a single year of foreign work experience with strong language skills,
 * return 25 points.
 *
 * @param {number} foreignWorkYears - Number of years of foreign work experience.
 * @param {number} aggregateClb - The primary language aggregate.
 * @returns {number} - Points (max 25) for this factor.
 */
export function skillTransferabilityForeignWorkLanguage(foreignWorkYears, aggregateClb) {
  if (foreignWorkYears >= 1) {
    if (aggregateClb >= 9) return 25;
    // You can adjust if needed for lower language scores.
  }
  return 0;
}

export function skillTransferabilityCertificateForeignWork(hasCertificate, foreignWorkYears) {
  if (!hasCertificate) return 0;
  if (foreignWorkYears >= 2) return 50;
  if (foreignWorkYears === 1) return 25;
  return 0;
}

export function skillTransferabilityLanguageCanadianEducation(aggregateClb, hasCanadianEducation) {
  if (!hasCanadianEducation) return 0;
  if (aggregateClb >= 9) return 50;
  if (aggregateClb >= 7) return 25;
  return 0;
}

/**
 * NEW: Calculates the overall Skill Transferability Score.
 * It sums two sections:
 *  - Education factors: take the maximum of:
 *       a) Official Language proficiency and education (max 50)
 *       b) Canadian work experience and education (max 25)
 *  - Foreign work experience factors: sum of:
 *       a) Official Language proficiency and foreign work experience (max 25)
 *       b) Canadian and foreign work experience (max 13)
 *  - Certificate factor (not used here, so assumed 0)
 *
 * Expected subtotal for:
 *  - Education factors = 50
 *  - Foreign work experience factors = 38
 * Overall transferability = 50 + 38 = 88.
 *
 * @param {Object} formData - The applicant’s form data.
 * @returns {number} - The overall skill transferability score.
 */
export function calculateTransferabilityScore(formData) {
  // Education factors: take the maximum.
  const educationLanguage = skillTransferabilityEducationLanguage(formData.primaryLanguageAggregate, formData.education);
  const canadianWorkEducation = skillTransferabilityCanadianWorkAndEducation(formData.canadianWorkExperience, formData.education);
  const educationTransferability = Math.max(educationLanguage, canadianWorkEducation);
  // console.log("Education Transferability (max):", educationTransferability);

  // Foreign work experience factors: sum these two.
  const foreignWorkLang = skillTransferabilityForeignWorkLanguage(formData.foreignWorkExperience, formData.primaryLanguageAggregate);
  const canadianAndForeignWork = skillTransferabilityCanadianAndForeignWork(formData.canadianWorkExperience, formData.foreignWorkExperience);
  const foreignTransferability = foreignWorkLang + canadianAndForeignWork;
  // console.log("Foreign Work Transferability (sum):", foreignTransferability);

  // Certificate factor (if applicable) – not used here.
  const certificate = 0;

  const total = educationTransferability + foreignTransferability + certificate;
  // console.log("Total Skill Transferability Score:", total);
  return total;
}

// ---------- Additional Points ----------

export function additionalProvincialNominationPoints(isNominated) {
  return isNominated ? 600 : 0;
}

export function additionalJobOfferPoints(nocLevel) {
  if (nocLevel === "0-00") return 200;
  if (["0123"].includes(nocLevel)) return 50;
  return 0;
}

export function additionalCanadianEducationPoints(educationDuration) {
  if (educationDuration >= 3) return 30;
  if (educationDuration >= 1) return 15;
  return 0;
}

export function additionalSiblingPoints(hasSibling) {
  return hasSibling ? 15 : 0;
}

// adding dropdown options

export const languageTests = [
  { test: 'IELTS', label: 'IELTS (General Training)' },
  { test: 'CELPIP', label: 'CELPIP-General' },
  { test: 'TEF', label: 'TEF Canada' },
  { test: 'TCF', label: 'TCF Canada' },
  { test: 'PTE', label: 'PTE Core' },
];

export const testSkillMapping = {
  'IELTS': {
    reading: { 3.5: 4, 4: 5, 5: 6, 6: 7, 6.5: 8, 7: 9, 8: 10 },
    writing: { 4: 4, 5: 5, 5.5: 6, 6: 7, 6.5: 8, 7: 9, 7.5: 10 },
    listening: { 4.5: 4, 5: 5, 5.5: 6, 6: 7, 7.5: 8, 8: 9, 8.5: 10 },
    speaking: { 4: 4, 5: 5, 5.5: 6, 6: 7, 6.5: 8, 7: 9, 7.5: 10 },
  },
  'CELPIP': {
    reading: { 4: 4, 5: 5, 6: 6, 7: 7, 8: 8, 9: 9, 10: 10 },
    writing: { 4: 4, 5: 5, 6: 6, 7: 7, 8: 8, 9: 9, 10: 10 },
    listening: { 4: 4, 5: 5, 6: 6, 7: 7, 8: 8, 9: 9, 10: 10 },
    speaking: { 4: 4, 5: 5, 6: 6, 7: 7, 8: 8, 9: 9, 10: 10 },
  },
  'TEF': {
    reading: { 306: 4, 352: 5, 393: 6, 434: 7, 462: 8, 503: 9, 546: 10 },
    writing: { 268: 4, 330: 5, 379: 6, 428: 7, 472: 8, 512: 9, 558: 10 },
    listening: { 306: 4, 352: 5, 393: 6, 434: 7, 462: 8, 503: 9, 546: 10 },
    speaking: { 328: 4, 387: 5, 422: 6, 456: 7, 494: 8, 518: 9, 556: 10 },
  },
  'TCF': {
    reading: { 342: 4, 375: 5, 406: 6, 453: 7, 499: 8, 524: 9, 549: 10 },
    writing: { 4: 4, 6: 5, 7: 6, 10: 7, 12: 8, 14: 9, 16: 10 },
    listening: { 331: 4, 369: 5, 398: 6, 458: 7, 503: 8, 523: 9, 549: 10 },
    speaking: { 4: 4, 6: 5, 7: 6, 10: 7, 12: 8, 14: 9, 16: 10 },
  },
  'PTE': {
  reading: { 33: 4, 42: 5, 51: 6, 60: 7, 69: 8, 78: 9, 88: 10 },
  writing: { 41: 4, 51: 5, 60: 6, 69: 7, 79: 8, 88: 9, 90: 10 },
  listening: { 28: 4, 39: 5, 50: 6, 60: 7, 71: 8, 82: 9, 89: 10 },
  speaking: { 42: 4, 51: 5, 59: 6, 68: 7, 76: 8, 84: 9, 89: 10 },
},
}

export const languageOptions = [
  {value: 0, label: "N/A"},
  {value: 6, label: "CLB 6"},
  {value: 7, label: "CLB 7"},
  {value: 8, label: "CLB 8"},
  {value: 9, label: "CLB 9 or higher"}
]

export const educationCanadaOptions = [
  { value: 0,    label: "Secondary (high school) or less" },
  { value: 88,   label: "1– or 2-year diploma/certificate" },
  { value: 112,  label: "3+ year degree/diploma or 1+ year Master's/professional/doctoral" }
 ];

export const educationHighestOptions = [
  { value: 0,   label: "Less than secondary (high school)" },
  { value: 28,  label: "Secondary diploma (high school graduation)" },
  { value: 84,  label: "1-year post-secondary program" },
  { value: 91,  label: "2-year post-secondary program" },
  { value: 112, label: "Bachelor's degree (3+ year program)" },
  { value: 119, label: "Multiple post-secondary credentials (one must be 3+ years)" },
  { value: 126, label: "Master's or professional degree" },
  { value: 140, label: "Doctoral degree (PhD)" }
];

export const spouseEducationOptions = [
  { value: "N/A", label: "N/A"},
  { value: "Secondary diploma", label: "Secondary diploma (High School)" },
  { value: "One or two-year program", label: "One or two-year program at a college, trade school, or other institution" },
  { value: "Bachelor’s degree", label: "Bachelor’s degree (or equivalent)" },
  { value: "Two or more post-secondary programs", label: "Two or more post-secondary programs (at least one must be a Bachelor’s degree)" },
  { value: "Master’s degree", label: "Master’s degree (or equivalent)" },
  { value: "Doctoral degree", label: "Doctoral degree (Ph.D.)" },
];

export const workExperienceForeignOptions = [
  { value: 0, label: "None" },
  { value: 1, label: "1 year" },
  { value: 2, label: "2 years" },
  { value: 3, label: "3 years or more" }
];

export const workExperienceCanadaOptions = [
  { value: 0, label: "None" },
  { value: 1, label: "1 year" },
  { value: 2, label: "2 years" },
  { value: 3, label: "3 years" },
  { value: 4, label: "4 years" },
  { value: 5, label: "5 or more years" },
];

/**
 * French language bonus: if the applicant scores CLB 7 or higher in French,
 * then award 25 points; if French is strong and English is low (CLB ≤ 4), award 50.
 */
export function additionalFrenchLanguagePoints(frenchAggregateClb, englishAggregateClb) {
  if (frenchAggregateClb >= 7) {
    return englishAggregateClb <= 4 ? 50 : 25;
  }
  return 0;
}

/**
 * Existing Additional Score Calculation – using your other additional factors.
 * For this example, only job offer points contribute (50) as others are 0.
 *
 * @param {Object} formData - The applicant’s form data.
 * @returns {number} - The additional score.
 */
export function calculateAdditionalScore(formData) {
  const provincial = additionalProvincialNominationPoints(formData.isNominated); // Expected: 0
  const jobOffer = additionalJobOfferPoints(formData.nocLevel);              // Expected: 50
  const canadianEdu = additionalCanadianEducationPoints(formData.canadianEducationDuration); // 0
  const sibling = additionalSiblingPoints(formData.hasSibling);              // 0
  // French-language skills – assumed 0 if not provided.
  const french = additionalFrenchLanguagePoints(formData.frenchAggregateClb, formData.englishAggregateClb); // 0
  
  const totalAdditional = provincial + jobOffer + canadianEdu + sibling + french;
  // console.log("Additional Score Breakdown - Provincial:", provincial, "Job Offer:", jobOffer, "Canadian Edu:", canadianEdu, "Sibling:", sibling, "French:", french, "=> Total:", totalAdditional);
  return Math.min(totalAdditional, 600);
}



/**
 * NEW: Calculates the education transferability factor.
 * This represents the "Official Language proficiency and education" part.
 * For a single applicant with CLB 9 (or higher) and a high education level, return 50.
 *
 * @param {number} aggregateClb - The primary language aggregate (e.g., 9).
 * @param {number} education - The numeric education value.
 * @returns {number} - Points (max 50) for this factor.
 */
export function skillTransferabilityEducationLanguage(aggregateClb, education) {
  // For our purposes, if CLB is 9 or higher, award the full 50 points.
  if (aggregateClb >= 9) {
    return 50;
  }
  // Otherwise, you could implement a graduated scale.
  return 0;
}

/**
 * NEW: Calculates the Canadian work experience and education factor.
 * For a single year of Canadian work experience, return 25 points.
 *
 * @param {number} canadianWorkYears - Number of years of Canadian work experience.
 * @param {number} education - The numeric education value.
 * @returns {number} - Points (up to 25) for this factor.
 */
export function skillTransferabilityCanadianWorkAndEducation(canadianWorkYears, education) {
  if (canadianWorkYears === 1) return 25;
  if (canadianWorkYears >= 2) return 50;
  return 0;
}

/**
 * NEW: Calculates the Canadian & foreign work experience factor.
 * For a single year of foreign work experience, return 13 points.
 *
 * @param {number} canadianWorkYears - Canadian work experience years.
 * @param {number} foreignWorkYears - Foreign work experience years.
 * @returns {number} - Points (max 13) for this factor.
 */
export function skillTransferabilityCanadianAndForeignWork(canadianWorkYears, foreignWorkYears) {
  if (foreignWorkYears === 1) return 13;
  if (foreignWorkYears >= 2) return 25;
  return 0;
}


/**
 * Calculates the Comprehensive Ranking System (CRS) Score.
 * Expected breakdown with your sample data:
 *   Core = 412
 *   Spouse = 0
 *   Skill Transferability = 88
 *   Additional = 50
 * Grand Total = 412 + 0 + 88 + 50 = 550.
 *
 * @param {Object} formData - The applicant’s form data.
 * @returns {number} - The final CRS score.
 */
export function calculateCRSScore(formData) {
  const core = calculateCoreScore(formData);
  const spouse = calculateSpouseScore(formData); // For single applicants, this returns 0.
  const transferability = calculateTransferabilityScore(formData);
  const additional = calculateAdditionalScore(formData);
  
  // console.log("=== CRS Score Breakdown ===");
  // console.log("Core Score:", core);
  // console.log("Spouse Score:", spouse);
  // console.log("Transferability Score:", transferability);
  // console.log("Additional Score:", additional);

  const totalScore = core + spouse + transferability + additional;
  // console.log("Total CRS Score:", totalScore);
  
  return totalScore;
}


// ---------- Final Comprehensive CRS Score ----------

// export function calculateCRSScore(formData) {
//   // Calculate each component of the CRS score.
//   const core = calculateCoreScore(formData);
//   const spouse = calculateSpouseScore(formData);
//   const transferability = calculateTransferabilityScore(formData);
//   const additional = calculateAdditionalScore(formData);
  
//   // Log each component's score for debugging.
//   console.log("=== CRS Score Breakdown ===");
//   console.log("Core Score:", core);
//   console.log("Spouse Score:", spouse);
//   console.log("Transferability Score:", transferability);
//   console.log("Additional Score:", additional);

//   // Compute and log the final CRS score.
//   const totalScore = core + spouse + transferability + additional;
//   console.log("Total CRS Score:", totalScore);
  
//   return totalScore;
// }


